/**
 * src/pages/Dashboard.js  (REPLACE)
 *
 * WHAT CHANGED (internals only — zero UI changes):
 * - authFetch calls replaced with useDashboard hook
 * - stats, upcomingMilestones, resourceAllocation from Redux store
 * - projects from Redux store (passed via Outlet context as before)
 * - fetchDashboard(sortBy) still works via Outlet context (calls loadProjects)
 */

import { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useOutletContext, useNavigate } from "react-router-dom";
import { Icons } from "../components/icons";
import { ChevronDown } from "lucide-react";
import useDashboard from "../hooks/useDashboard";
import useOrganisation from "../hooks/useOrganisation";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

const getProfileImageUrl = (member) => {
  if (!member) return "";
  if (typeof member.profilePicture === "string") return member.profilePicture;
  if (member.profilePicture?.url) return member.profilePicture.url;
  if (typeof member.avatar === "string") return member.avatar;
  if (typeof member.profileImage === "string") return member.profileImage;
  return "";
};

// Sort order for "Status" — most urgent/attention-needed first.
const TRACKING_STATUS_RANK = {
  "at-risk": 0,
  "on-hold": 1,
  "on-track": 2,
  completed: 3,
};

// Sort order for "Priority" — highest priority first.
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

// ─── Organisations card ─────────────────────────────────────────────
// Sits below the Project Overview table and is visible to admins only.
// Shows a centred empty state until the first organisation is formed, after
// which the create action moves to the card's top-right corner.
function OrganisationCard() {
  const navigate = useNavigate();
  const { currentUser } = useOutletContext();
  const { organisations, isFetchingOrganisations, loadOrganisations } =
    useOrganisation();

  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    if (isAdmin) loadOrganisations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!isAdmin) return null;

  // Newest first — the API already sorts this way; slice for the summary.
  const recent = (organisations || []).slice(0, 4);

  return (
    <div className="bg-white p-4 md:p-6 rounded-[12px] shadow-[0_1px_3px_rgba(16,24,40,0.06)] hover:shadow-[0_4px_12px_rgba(16,24,40,0.10)] transition-shadow duration-200 mt-3 sm:mt-4 md:mt-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-0 border-b border-[#EDF0F5] pb-3">
        <div>
          <h2 className="text-[16px] font-semibold text-[#111827]">
            Workspaces &amp; Organisation
          </h2>
          <p className="text-[11px] text-[#8A94A6] mt-0.5">
            Connected sub-organisations and client partitions
          </p>
        </div>

        <div className="flex items-center gap-4">
          {organisations.length > 0 && (
            <>
              <span
                className="text-[#4F46E5] text-[13px] font-medium cursor-pointer hover:underline flex items-center gap-1"
                onClick={() => navigate("/dashboard/organisation")}
              >
                View All
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
              </span>
              <button
                onClick={() => navigate("/dashboard/organisation")}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#4253C7] text-white"
                aria-label="Add organisation"
                title="Add organisation"
              >
                <Icons.Organisation className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {isFetchingOrganisations ? (
        <p className="text-center text-gray-400 py-8 text-sm">Loading…</p>
      ) : organisations.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center w-full py-12 gap-4">
          <Icons.Organisation className="text-gray-300 w-10 h-10" />
          <p className="text-gray-400 text-sm">No organisation formed</p>
          <button
            onClick={() => navigate("/dashboard/organisation")}
            className="flex items-center gap-2 bg-[#4253C7] text-white px-5 h-[40px] text-sm rounded-lg"
          >
            <Icons.Organisation className="w-4 h-4" />
            Form New Organisation
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {recent.slice(0, 2).map((org) => (
            <button
              key={org._id}
              onClick={() => navigate(`/dashboard/organisation/${org._id}`)}
              className="text-left border border-[#EAECF0] hover:border-[#4F46E5] hover:bg-[#FAFBFF] rounded-[10px] p-4 transition-colors"
            >
              <div className="flex items-start gap-2.5">
                <span className="shrink-0 flex h-8 w-8 items-center justify-center rounded-[8px] bg-[#F4F6FA]">
                  <Icons.Organisation className="text-[#6B7280] w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-[#111827] truncate">
                    {org.name}
                  </p>
                  <p className="text-[11px] text-[#8A94A6] mt-0.5">
                    {org.projectCount} project
                    {org.projectCount === 1 ? "" : "s"} · {org.clientCount}{" "}
                    client{org.clientCount === 1 ? "" : "s"}
                  </p>
                </div>
              </div>
            </button>
          ))}
          {/* A dashed tile to add another, as in the design. */}
          <button
            onClick={() => navigate("/dashboard/organisation")}
            className="flex items-center justify-center gap-2 rounded-[10px] border border-dashed border-[#D6DBE3] p-4 text-[13px] font-medium text-[#6B7280] transition-colors hover:border-[#4F46E5] hover:bg-[#FAFBFF] hover:text-[#4F46E5]"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="h-4 w-4" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Add Organisation
          </button>
        </div>
      )}
    </div>
  );
}

const Dashboard = ({ cardsData = null }) => {
  const { projects } = useOutletContext();
  const [sortOpen, setSortOpen] = useState(false);
  // Sorting is done entirely client-side against whatever the backend
  // returns — "status" and "completion" have no server-side sort support,
  // so all three criteria are applied here for consistency.
  const [sortBy, setSortBy] = useState("");

  const sortedProjects = useMemo(() => {
    const list = [...(projects || [])];
    if (sortBy === "status") {
      list.sort(
        (a, b) =>
          (TRACKING_STATUS_RANK[a.trackingStatus] ?? 99) -
          (TRACKING_STATUS_RANK[b.trackingStatus] ?? 99),
      );
    } else if (sortBy === "completion") {
      list.sort((a, b) => (b.progress ?? 0) - (a.progress ?? 0));
    } else if (sortBy === "priority") {
      list.sort(
        (a, b) =>
          (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99),
      );
    }
    return list;
  }, [projects, sortBy]);

  const [showCards, setShowCards] = useState(true);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [viewedProject, setViewedProject] = useState(null);
  // Card click → filter Project Overview by tracking status
  const [statusFilter, setStatusFilter] = useState(null);
  const navigate = useNavigate();

  // Project Overview's actual visible rows — same source for the desktop
  // table, the mobile card list, and the empty-state check below.
  const visibleProjects = useMemo(() => {
    const base = selectedProjectId
      ? sortedProjects.filter((p) => p._id === selectedProjectId)
      : sortedProjects;
    return base.filter(
      (p) => !statusFilter || p.trackingStatus === statusFilter,
    );
  }, [sortedProjects, selectedProjectId, statusFilter]);

  // By default only show as many project rows as fit the screen height;
  // "View All" reveals the full list.
  const [showAllProjects, setShowAllProjects] = useState(false);
  const computeVisibleCount = () =>
    Math.max(3, Math.floor((window.innerHeight - 430) / 52));
  const [visibleProjectCount, setVisibleProjectCount] =
    useState(computeVisibleCount);
  useEffect(() => {
    const onResize = () => setVisibleProjectCount(computeVisibleCount());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ── Redux: replace the two authFetch calls ────────────────────────
  const {
    stats,
    upcomingMilestones,
    resourceAllocation,
    weeklyProgress,
    overallCompletion,
    isDashboardLoading,
    loadDashboard,
    loadGraphs,
  } = useDashboard();

  // Each stat card must equal the number of rows clicking it produces in
  // Project Overview, so both are counted from the SAME array the table
  // filters — `sortedProjects`. Deriving them from `stats` (/api/dashboard)
  // instead would be more accurate but would not match the list: the backend
  // computes tracking status from live task deadlines, while /api/projects
  // returns only the stored `status` field, so a project that is at-risk
  // purely because a task is overdue cannot be identified client-side.
  //
  // Note this counts `sortedProjects`, not `visibleProjects` — the latter is
  // also narrowed by the VIEW button (`selectedProjectId`), which would drive
  // every card to 1 while a single project is expanded. Card counts track the
  // status filter only.
  const statusCounts = useMemo(() => {
    const counts = {
      total: sortedProjects.length,
      "on-track": 0,
      "at-risk": 0,
      completed: 0,
    };
    sortedProjects.forEach((p) => {
      if (counts[p.trackingStatus] !== undefined) counts[p.trackingStatus] += 1;
    });
    return counts;
  }, [sortedProjects]);

  // Projects arrive via the Outlet context, so on a hard refresh the list is
  // briefly empty. Show a placeholder rather than a confident "0" that would
  // immediately be replaced — that swap was the flicker.
  const hasProjects = (projects || []).length > 0;
  const statCardValue = (value) => {
    if (hasProjects) return value;
    return isDashboardLoading ? "—" : 0;
  };

  useEffect(() => {
    loadDashboard();
    loadGraphs(); // initial load — all projects
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedProjectId) {
      loadGraphs(selectedProjectId); // per-project graphs
    }
  }, [selectedProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sort handler ──────────────────────────────────────────────────
  // Purely client-side — sorts whatever `projects` already holds by
  // Status / Completion / Priority (see sortedProjects above).
  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  // ── Everything below is 100% identical to your original ──────────

  // The small initials badge at the start of each Project Overview row.
  //
  // Colour is keyed to the project's id so a project keeps the same badge
  // between renders and re-sorts; the row index is only a fallback for a
  // project that somehow has no id.
  const PROJECT_BADGES = [
    { tint: "#EEF2FF", color: "#4F46E5" },
    { tint: "#ECFDF3", color: "#027A48" },
    { tint: "#FEF3F2", color: "#D92D20" },
    { tint: "#FFF6ED", color: "#C4320A" },
    { tint: "#F4F3FF", color: "#6941C6" },
    { tint: "#EFF8FF", color: "#175CD3" },
  ];
  const projectBadge = (project, index) => {
    const name = String(project?.name || "").trim();
    // Two letters: first letters of the first two words, else the first two
    // characters — "GLAD GEN Setu" gives GS, "glad 2" gives G2.
    const words = name.split(/\s+/).filter(Boolean);
    const initials = (
      words.length >= 2
        ? words[0][0] + words[1][0]
        : name.slice(0, 2) || "??"
    ).toUpperCase();

    const key = String(project?._id || index);
    let hash = 0;
    for (let n = 0; n < key.length; n += 1) {
      hash = (hash * 31 + key.charCodeAt(n)) >>> 0;
    }
    return { initials, ...PROJECT_BADGES[hash % PROJECT_BADGES.length] };
  };

  // Percentage of the portfolio a status accounts for — the design shows
  // this under "On Track" rather than a fixed phrase.
  const portfolioShare = (n) => {
    const total = statusCounts.total || 0;
    if (!total) return 0;
    return Math.round((n / total) * 100);
  };

  const defaultCards = [
    {
      id: "card-1",
      title: "Total Projects",
      status: null,
      font: "text-medium",
      value: statCardValue(statusCounts.total),
      description: (
        <div className="flex items-center gap-1">
          <Icons.Garrow className="text-[#12B76A] w-3.5 h-3.5" />
          <span className="font-normal">
            +{stats?.newThisMonth ?? 0} this month
          </span>
        </div>
      ),
      border: "border-[#6366F1]",
      accent: "#000000",
      // No coloured tile behind this icon; its outline is drawn in black.
      tint: "transparent",
      valueColor: "text-[#111827]",
      // The shared Projects icon is drawn in white for the sidebar, so recolour
      // its strokes here only.
      icon: (
        <span className="flex [&_path]:stroke-black">
          <Icons.Projects />
        </span>
      ),
    },
    {
      id: "card-2",
      title: "On Track",
      status: "on-track",
      font: "text-medium",
      value: statCardValue(statusCounts["on-track"]),
      description: (
        <div className="flex items-center gap-1">
          <Icons.Track className="text-[#3B82F6] w-3.5 h-3.5" />
          <span className="font-normal">
            {portfolioShare(statusCounts["on-track"])}% of portfolio
          </span>
        </div>
      ),
      border: "border-[#3B82F6]",
      accent: "#3B82F6",
      tint: "#EFF6FF",
      valueColor: "text-[#1D4ED8]",
      icon: <Icons.Track className="w-4 h-4" />,
    },
    {
      id: "card-3",
      title: "At Risk",
      status: "at-risk",
      font: "text-medium",
      value: statCardValue(statusCounts["at-risk"]),
      description: (
        <div className="flex items-center gap-1">
          <Icons.Risk className="text-[#F04438] w-3.5 h-3.5" />
          <span className="font-normal">Needs immediate triage</span>
        </div>
      ),
      border: "border-[#F04438]",
      accent: "#F04438",
      tint: "#FEF3F2",
      valueColor: "text-[#D92D20]",
      icon: <Icons.Risk className="w-4 h-4" />,
    },
    {
      id: "card-4",
      title: "Completed Goals",
      status: "completed",
      font: "text-medium",
      value: statCardValue(statusCounts.completed),
      description: (
        <div className="flex items-center gap-1">
          <Icons.Complete className="text-[#12B76A] w-3.5 h-3.5" />
          <span className="font-normal">
            {portfolioShare(statusCounts.completed)}% velocity rate
          </span>
        </div>
      ),
      border: "border-[#12B76A]",
      accent: "#12B76A",
      tint: "#ECFDF3",
      valueColor: "text-[#027A48]",
      icon: <Icons.Complete className="w-4 h-4" />,
    },
  ];

  const cards = cardsData || defaultCards;
  const getPriorityBadge = (priority) => {
    const p = (priority || "").toUpperCase();

    if (p === "LOW") {
      return "bg-[#CFFFDE] text-[#003C18]";
    }

    if (p === "MEDIUM") {
      return "bg-[#FFE8B6] text-[#B76E00]";
    }

    if (p === "HIGH") {
      return "bg-[#FFCA75CC] text-[#FF1A00]";
    }

    if (p === "CRITICAL") {
      return "bg-[#FFD6D6] text-[#D60000]";
    }

    return "bg-blue-100 text-blue-700";
  };

  const formatPriority = (priority) => {
    if (!priority) return "";
    return priority.charAt(0).toUpperCase() + priority.slice(1).toLowerCase();
  };

  const getStatusDotColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "at-risk") return "bg-red-500";
    if (s === "on-track") return "bg-blue-500";
    if (s === "completed") return "bg-green-500";
    if (s === "on-hold") return "bg-yellow-500";
    return "bg-gray-400";
  };

  const getStatusBarColor = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "at-risk") return "bg-red-500";
    if (s === "on-track") return "bg-blue-500";
    if (s === "completed") return "bg-green-500";
    if (s === "on-hold") return "bg-yellow-500";
    return "bg-gray-400";
  };

  return (
    <div className="p-3 sm:p-4 md:p-6 w-full">
      {/* ===== CARDS =====
          Four stat tiles. Each carries a coloured left border and a tinted
          icon badge, so the row reads at a glance without the numbers
          themselves being coloured shouting. */}
      {showCards && (
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 auto-rows-max">
          {cards.map((card) => (
            <div
              key={card.id}
              onClick={() =>
                setStatusFilter((prev) =>
                  card.status && prev !== card.status ? card.status : null,
                )
              }
              className={`bg-white w-full h-auto rounded-[12px] p-4 md:p-5 shadow-[0_1px_3px_rgba(16,24,40,0.06)] hover:shadow-[0_4px_12px_rgba(16,24,40,0.10)] transition-shadow duration-200 border-l-[3px] cursor-pointer flex flex-col justify-between min-h-fit ${card.border}`}
            >
              {/* Label on the left, tinted icon badge on the right — the
                  badge carries the card's colour so the number itself can
                  stay clean. */}
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8A94A6] leading-tight break-words">
                  {card.title}
                </h3>
                {card.icon && (
                  <span
                    className="shrink-0 flex h-7 w-7 items-center justify-center rounded-[8px]"
                    style={{ backgroundColor: card.tint, color: card.accent }}
                    aria-hidden="true"
                  >
                    {card.icon}
                  </span>
                )}
              </div>
              <p
                className={`text-[28px] md:text-[32px] font-bold mt-2 leading-none ${card.valueColor || "text-[#111827]"}`}
                style={{ fontFamily: "'Work Sans', sans-serif" }}
              >
                {card.value}
              </p>
              <p className="text-[12px] text-[#6B7280] mt-2.5 font-normal flex items-center">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* ===== MAIN GRID ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mt-6 sm:mt-7">
        {/* LEFT SIDE */}
        <div className="lg:col-span-3">
          <div className="bg-white p-3 sm:p-4 md:p-6 rounded-[12px] shadow-[0_1px_3px_rgba(16,24,40,0.06)] hover:shadow-[0_4px_12px_rgba(16,24,40,0.10)] transition-shadow duration-200 overflow-x-auto">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3 sm:gap-0 border-b border-[#EDF0F5] pb-3">
              {/* Title with a count pill beside it, as in the design. */}
              <div className="flex-1 flex items-center gap-2.5">
                <h2 className="text-[16px] font-semibold text-[#111827]">
                  Project Overview
                </h2>
                <span className="rounded-full bg-[#EEF2FF] px-2.5 py-[2px] text-[11px] font-medium text-[#4F46E5]">
                  {visibleProjects.length} Active
                </span>
              </div>

              <div className="flex items-center gap-4">
                <span
                  className="text-[#4F46E5] text-[13px] font-medium cursor-pointer hover:underline"
                  onClick={() => {
                    setShowCards((prev) => !prev);
                    setShowAllProjects((prev) => !prev);
                  }}
                >
                  {showAllProjects
                    ? "View Less"
                    : `View All (${sortedProjects.length})`}
                </span>

                <div
                  className="relative w-full sm:w-auto"
                  onClick={() => setSortOpen((prev) => !prev)}
                >
                  <select
                    className="appearance-none text-xs sm:text-sm border rounded-md px-3 py-1 bg-gray-200 min-w-[130px] outline-none"
                    onChange={handleSortChange}
                    value={sortBy}
                    onBlur={() => setTimeout(() => setSortOpen(false), 150)}
                  >
                    <option value="">Sort By</option>
                    <option value="status">Status</option>
                    <option value="completion">Completion</option>
                    <option value="priority">Priority</option>
                  </select>

                  <div
                    className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none transition-transform duration-200 ${
                      sortOpen ? "rotate-180" : "rotate-0"
                    }`}
                  >
                    <ChevronDown size={14} />
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-[2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.8fr] text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8A94A6] mb-3 gap-x-4 px-2">
              <span>PROJECT</span>
              <span className="text-center">COMPLETION</span>
              <span className="text-center">PRIORITY</span>
              <span className="text-center">PROGRESS</span>
              <span className="text-center">STATUS</span>
              <span className="text-center">ACTION</span>
            </div>

            {(sortedProjects || []).length === 0 ? (
              <div className="grid grid-cols-[2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.8fr] text-center text-gray-400 py-4 sm:py-6 text-xs sm:text-sm gap-x-4 px-2">
                No projects yet. Create one 🚀
              </div>
            ) : visibleProjects.length === 0 ? (
              <div className="flex items-center justify-center text-center text-gray-400 py-10 sm:py-14 text-sm">
                No records found
              </div>
            ) : (
              visibleProjects
                .slice(0, showAllProjects ? undefined : visibleProjectCount)
                .map((p, i) => {
                  const progressValue = p.progress ?? 0;
                  return (
                    <div
                      key={p._id || i}
                      onClick={() => navigate(`/dashboard/projects/${p._id}`)}
                      className="hidden md:grid grid-cols-[2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.8fr] items-center py-3 text-xs sm:text-sm gap-x-4 px-2 cursor-pointer border-b border-[#F4F6F9] last:border-b-0 hover:bg-[#FAFBFC] transition-colors"
                    >
                      {/* Initials badge + name, matching the design. The
                          badge colour is keyed to the row so each project
                          keeps the same one between renders. */}
                      <span className="flex items-center gap-2.5 min-w-0">
                        <span
                          className="shrink-0 flex h-8 w-8 items-center justify-center rounded-[8px] text-[11px] font-bold"
                          style={{
                            backgroundColor: projectBadge(p, i).tint,
                            color: projectBadge(p, i).color,
                          }}
                          aria-hidden="true"
                        >
                          {projectBadge(p, i).initials}
                        </span>
                        <span className="min-w-0">
                          <span
                            className="block truncate text-[13px] font-semibold text-[#111827]"
                            title={p.name}
                          >
                            {p.name}
                          </span>
                          {p.description && (
                            <span
                              className="block truncate text-[11px] text-[#8A94A6]"
                              title={p.description}
                            >
                              {p.description}
                            </span>
                          )}
                        </span>
                      </span>
                      <span className="text-center text-[13px] font-medium text-[#374151]">
                        {progressValue}%
                      </span>
                      <span className="text-center">
                        <span
                          className={`px-2.5 py-[3px] text-[10px] font-semibold rounded-full inline-flex justify-center ${getPriorityBadge(p.priority)}`}
                        >
                          {formatPriority(p.priority).toUpperCase()}
                        </span>
                      </span>
                      <span className="flex justify-center">
                        <div className="w-2/3 bg-[#EDF0F5] h-[6px] rounded-full overflow-hidden">
                          <div
                            className={`h-[6px] rounded-full transition-all duration-500 ${getStatusBarColor(p.trackingStatus)}`}
                            style={{ width: `${progressValue}%` }}
                          ></div>
                        </div>
                      </span>
                      <span className="text-center capitalize flex items-center justify-center gap-2 text-[12px] text-[#374151]">
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${getStatusDotColor(p.trackingStatus)}`}
                        ></span>
                        {(p.trackingStatus || "").replace(/-/g, " ")}
                      </span>
                      <span className="flex justify-center">
                        <button
                          className="text-[11px] font-medium px-3 py-1.5 rounded-[6px] border border-[#E4E7EC] text-[#475467] hover:bg-[#F7F8FA] transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (selectedProjectId === p._id) {
                              setSelectedProjectId(null);
                              setViewedProject(null);
                            } else {
                              setSelectedProjectId(p._id);
                              setViewedProject(p); // Capture the project details
                            }
                          }}
                        >
                          {selectedProjectId === p._id ? "CLOSE" : "VIEW"}
                        </button>
                      </span>
                    </div>
                  );
                })
            )}

            {(sortedProjects || []).length > 0 &&
              visibleProjects.length === 0 && (
                <div className="md:hidden text-center text-gray-400 py-10 text-sm">
                  No records found
                </div>
              )}

            {visibleProjects.length > 0 && (
              <div className="md:hidden space-y-3">
                {visibleProjects.map((p, i) => {
                  const progressValue = p.progress ?? 0;
                  return (
                    <div
                      key={p._id || i}
                      onClick={() => navigate(`/dashboard/projects/${p._id}`)}
                      className="border rounded-lg p-3 bg-gray-50 cursor-pointer"
                    >
                      <div className="flex justify-between items-start gap-2 mb-3">
                        <span className="font-medium text-sm truncate">
                          {p.name}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs rounded-full whitespace-nowrap ${getPriorityBadge(p.priority)}`}
                        >
                          {formatPriority(p.priority)}
                        </span>
                      </div>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Completion</span>
                          <span className="font-medium">{progressValue}%</span>
                        </div>
                        <div className="w-full bg-gray-200 h-2 rounded-full">
                          <div
                            className={`h-2 rounded-full ${getStatusBarColor(p.trackingStatus)}`}
                            style={{ width: `${progressValue}%` }}
                          ></div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status</span>
                          <span className="font-medium capitalize flex items-center gap-2">
                            <span
                              className={`w-2 h-2 rounded-full ${getStatusDotColor(p.trackingStatus)}`}
                            ></span>
                            {(p.trackingStatus || "").replace(/-/g, " ")}
                          </span>
                        </div>
                      </div>
                      <button
                        className="w-full mt-3 px-3 py-2 text-xs bg-gray-100 rounded-md hover:bg-gray-200 min-h-9"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (selectedProjectId === p._id) {
                            setSelectedProjectId(null);
                            setViewedProject(null);
                          } else {
                            setSelectedProjectId(p._id);
                            setViewedProject(p); // Capture the project details
                          }
                        }}
                      >
                        {selectedProjectId === p._id ? "CLOSE" : "VIEW"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {selectedProjectId && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mt-6">
              {/* ── WEEKLY PROGRESS LINE CHART ── */}
              <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm">
                <h3 className="font-semibold text-sm sm:text-base mb-1">
                  Weekly Progress
                </h3>
                <p className="text-xs text-gray-400 mb-4">
                  {viewedProject ? viewedProject.name : "Project"} — last 7 days
                </p>

                {!weeklyProgress || weeklyProgress.length === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">
                    No data found for this timeline
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={180}>
                    <LineChart
                      data={weeklyProgress}
                      margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        domain={[0, 100]}
                        tick={{ fontSize: 11, fill: "#9CA3AF" }}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(v) => `${v}%`}
                      />
                      <Tooltip
                        formatter={(value) => [`${value}%`, "Progress"]}
                        contentStyle={{
                          fontSize: 12,
                          borderRadius: 8,
                          border: "1px solid #E5E7EB",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="avgProgress"
                        stroke="#4379FE"
                        strokeWidth={2.5}
                        dot={{ r: 4, fill: "#4379FE" }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>
              {/* ── TASK STATUS/COMPLETION DONUT CHART ── */}
              <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm">
                <h3 className="font-semibold text-sm sm:text-base mb-1">
                  {selectedProjectId ? "Task Breakdown" : "Overall Completion"}
                </h3>
                <p className="text-xs text-gray-400 mb-2">
                  {viewedProject ? viewedProject.name : "All Projects"} — by
                  status
                </p>

                {!overallCompletion || overallCompletion.total === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-gray-400 text-sm">
                    No tasks or data found for this project
                  </div>
                ) : (
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={180}>
                      <PieChart>
                        {(() => {
                          // 1. Dynamic Key Color Mapping Setup
                          const COLOR_MAP = {
                            Completed: "#22C55E",
                            "In Progress": "#4379FE",
                            "On Track": "#4379FE",
                            "At Risk": "#EF4444",
                            Todo: "#F59E0B",
                            "On Hold": "#F59E0B",
                          };

                          // 2. Safely parse counts depending on whether a single project or global view is selected
                          const donutData = [
                            {
                              name: "Completed",
                              value:
                                overallCompletion.segments?.completed?.count ||
                                0,
                            },
                            {
                              name: selectedProjectId
                                ? "In Progress"
                                : "On Track",
                              value:
                                overallCompletion.segments?.inProgress?.count ||
                                overallCompletion.segments?.onTrack?.count ||
                                0,
                            },
                            {
                              name: "At Risk",
                              value:
                                overallCompletion.segments?.atRisk?.count || 0,
                            },
                            {
                              name: selectedProjectId ? "Todo" : "On Hold",
                              value:
                                overallCompletion.segments?.todo?.count ||
                                overallCompletion.segments?.onHold?.count ||
                                0,
                            },
                          ].filter((d) => d.value > 0); // Drop zero entries to avoid legend bloat

                          return (
                            <Pie
                              data={donutData}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={75}
                              paddingAngle={3}
                              dataKey="value"
                            >
                              {donutData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={COLOR_MAP[entry.name] || "#9CA3AF"}
                                />
                              ))}
                            </Pie>
                          );
                        })()}
                        <Tooltip
                          formatter={(value, name) => [
                            `${value} Item${value !== 1 ? "s" : ""}`,
                            name,
                          ]}
                          wrapperStyle={{ zIndex: 1000 }}
                          contentStyle={{
                            fontSize: 13,
                            fontWeight: 600,
                            borderRadius: 8,
                            border: "1px solid #E5E7EB",
                            padding: "10px 14px",
                            boxShadow:
                              "0 10px 25px -5px rgba(0,0,0,0.15), 0 8px 10px -6px rgba(0,0,0,0.1)",
                          }}
                          itemStyle={{ fontSize: 13, fontWeight: 600 }}
                          labelStyle={{ fontSize: 13, fontWeight: 700 }}
                        />
                        <Legend
                          iconType="circle"
                          iconSize={8}
                          wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>

                    {/* Center text overlay */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-2xl font-bold text-gray-800">
                        {overallCompletion.overallPercent ?? 0}%
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {selectedProjectId ? "tasks done" : "overall"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 lg:mt-0 bg-white rounded-[12px] p-4 md:p-5 shadow-[0_1px_3px_rgba(16,24,40,0.06)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[16px] font-semibold text-[#111827]">
              Upcoming Milestones
            </h2>
            {upcomingMilestones.length > 0 && (
              <span
                className="h-2 w-2 rounded-full bg-[#6366F1]"
                aria-hidden="true"
              />
            )}
          </div>
          {/* The list still scrolls; only its scrollbar is hidden, using the
              same three-engine pattern as the other scroll panes here. */}
          <div
            className={`overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${
              upcomingMilestones.length > 3 ? "h-[360px]" : "h-auto"
            }`}
          >
            {upcomingMilestones.length === 0 ? (
              <div className="text-center bg-white text-[#8A94A6] py-6 text-[13px]">
                No upcoming milestones
              </div>
            ) : (
              [...upcomingMilestones]
                .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
                .slice(0, 5)
                .map((milestone, i) => {
                  const deadlineDate = new Date(milestone.deadline);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const diffDays = Math.ceil(
                    (deadlineDate - today) / (1000 * 60 * 60 * 24),
                  );
                  const formattedDate = deadlineDate.toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                    },
                  );
                  const daysLabel =
                    diffDays === 0
                      ? "Due today"
                      : diffDays < 0
                        ? "Overdue"
                        : `${diffDays} day${diffDays !== 1 ? "s" : ""} left`;

                  return (
                    <div
                      key={milestone.taskId || i}
                      className="py-3 border-b border-[#F4F6F9] last:border-b-0"
                    >
                      {/* Project name on the left, date pill hard right. */}
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-[13px] font-semibold text-[#4F46E5] truncate min-w-0">
                          {milestone.projectName}
                        </p>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-2 py-[3px] rounded-[6px] whitespace-nowrap ${
                            diffDays <= 0
                              ? "bg-[#FEF3F2] text-[#B42318]"
                              : diffDays === 1
                                ? "bg-[#FFF6ED] text-[#C4320A]"
                                : "bg-[#FFFAEB] text-[#B54708]"
                          }`}
                        >
                          {formattedDate} &middot; {daysLabel}
                        </span>
                      </div>
                      {/* Task name, then the owner's initials on the right —
                          the design's small avatar. */}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-[11px] text-[#6B7280] truncate min-w-0">
                          {milestone.taskName}
                        </span>
                        <span
                          className="shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-[#EEF2FF] text-[9px] font-bold text-[#4F46E5]"
                          title={milestone.assigneeName || milestone.projectName}
                          aria-hidden="true"
                        >
                          {projectBadge(
                            { name: milestone.assigneeName || milestone.projectName, _id: milestone.taskId },
                            i,
                          ).initials}
                        </span>
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>

        {/* Resource Allocation is its own card, not a section inside the
            milestones one — they are unrelated, and sharing a surface made
            them read as a single panel. */}
        {selectedProjectId && (
          <div className="lg:col-start-4 bg-white p-4 md:p-5 rounded-[12px] shadow-[0_1px_3px_rgba(16,24,40,0.06)] mt-4 lg:mt-4">
            <h3 className="text-[16px] font-semibold text-[#111827] mb-4 pb-3 border-b border-[#EDF0F5]">
              Resource Allocation
            </h3>

              {resourceAllocation.length === 0 ? (
                <p className="text-xs text-gray-400">No data available</p>
              ) : (
                (() => {
                  const selectedProject = resourceAllocation.find(
                    (p) =>
                      p.projectId === selectedProjectId ||
                      p._id === selectedProjectId,
                  );
                  return selectedProject ? (
                    <div className="space-y-6">
                      {/* Team Members Section */}
                      {selectedProject.members &&
                        selectedProject.members.length > 0 && (
                          <div className="space-y-5">
                            {selectedProject.members.map((member, mi) => {
                              const totalAssigned =
                                member.totalTasks ??
                                member.taskCount ??
                                member.tasksAssigned ??
                                null;
                              const hasNoTasks =
                                totalAssigned === 0 ||
                                (totalAssigned === null &&
                                  member.tasksCompletedPct == null &&
                                  member.contribution == null);
                              const pct = hasNoTasks
                                ? 0
                                : (member.tasksCompletedPct ??
                                  member.contribution ??
                                  0);
                              const barColor =
                                pct >= 75
                                  ? "bg-blue-500"
                                  : pct >= 50
                                    ? "bg-green-500"
                                    : "bg-red-500";
                              const img = getProfileImageUrl(member);
                              return (
                                // Avatar on the left; beside it a column with
                                // the name and percentage on one line and the
                                // bar full-width beneath. Nothing has a fixed
                                // width, so the row fits any card width — the
                                // name truncates rather than squeezing the bar.
                                <div
                                  key={`${selectedProject.projectId}-${mi}`}
                                  className="flex items-center gap-3"
                                >
                                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gray-200 flex-shrink-0 overflow-hidden flex items-center justify-center text-xs font-semibold text-gray-500">
                                    {img ? (
                                      <img
                                        src={img}
                                        alt={member.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      getInitials(member.name)
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-baseline justify-between gap-2">
                                      <span
                                        className="min-w-0 truncate text-[13px] text-[#111827] font-medium"
                                        title={member.name}
                                      >
                                        {member.name}
                                      </span>
                                      <span className="shrink-0 text-[11px] font-semibold text-[#374151] tabular-nums">
                                        {hasNoTasks ? "-" : `${pct}%`}
                                      </span>
                                    </div>
                                    <div className="mt-1.5 relative w-full bg-[#EDF0F5] h-1.5 rounded-full overflow-hidden">
                                      {!hasNoTasks && (
                                        <div
                                          className={`h-full rounded-full ${barColor}`}
                                          style={{ width: `${pct}%` }}
                                        ></div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">
                      No resource allocation data for this project
                    </p>
                  );
                })()
              )}
          </div>
        )}
      </div>

      {/* ===== ORGANISATIONS (admin only) ===== */}
      <OrganisationCard />
    </div>
  );
};

Dashboard.propTypes = { cardsData: PropTypes.array };
export default Dashboard;
